import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlayerContext } from '../PlayerContext';
import { useStore } from '../hooks/useStore';
import { VolumeHighIcon, VolumeLowIcon, VolumeMuteIcon, GripIcon } from '../icons';
import { Tooltip } from './Tooltip';
import styles from './VolumeControl.module.css';

export function VolumeControl() {
  const { store, eventBus } = usePlayerContext();
  const volume = useStore(store, useCallback(s => s.volume, []));
  const isMuted = useStore(store, useCallback(s => s.isMuted, []));
  const trackRef = useRef<HTMLDivElement>(null);
  const sliderWrapRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number | null>(null);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  // Suppresses the mute-click that fires right after a touch opens the slider
  const skipNextMuteRef = useRef(false);

  // Cleanup drag listeners on unmount
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleMuteClick = useCallback(() => {
    if (skipNextMuteRef.current) {
      skipNextMuteRef.current = false;
      return;
    }
    eventBus.emit('muted:change', !store.getState().isMuted);
  }, [eventBus, store]);

  // Touch: toggle slider open/closed; suppress the subsequent mute click when opening
  const handleVolumeContainerTouch = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-volume-slider]')) return;
    setIsSliderOpen(prev => {
      if (!prev) skipNextMuteRef.current = true;
      return !prev;
    });
  }, []);

  // Close slider on outside touch
  useEffect(() => {
    if (!isSliderOpen) return;
    const handleOutside = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-volume-control]')) {
        setIsSliderOpen(false);
      }
    };
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => document.removeEventListener('touchstart', handleOutside);
  }, [isSliderOpen]);

  const isVertical = useCallback((): boolean => {
    const el = sliderWrapRef.current;
    return el !== null && el.offsetHeight > el.offsetWidth;
  }, []);

  const getVolumeFromEvent = useCallback((clientX: number, clientY: number): number => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    let raw: number;
    if (isVertical()) {
      // Vertical: top = 1.0, bottom = 0.0
      raw = 1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    } else {
      raw = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    }
    return Math.round(raw * 100) / 100;
  }, [isVertical]);

  const emitVolumeWithMuteSync = useCallback((newVolume: number) => {
    eventBus.emit('volume:change', newVolume);
    const currentMuted = store.getState().isMuted;
    if (newVolume === 0 && !currentMuted) {
      eventBus.emit('muted:change', true);
    } else if (newVolume > 0 && currentMuted) {
      eventBus.emit('muted:change', false);
    }
  }, [eventBus, store]);

  const handleSliderDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    emitVolumeWithMuteSync(getVolumeFromEvent(e.clientX, e.clientY));

    const onMove = (ev: MouseEvent) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        emitVolumeWithMuteSync(getVolumeFromEvent(ev.clientX, ev.clientY));
        rafRef.current = null;
      });
    };
    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', cleanup);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      dragCleanupRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', cleanup);
    dragCleanupRef.current = cleanup;
  }, [emitVolumeWithMuteSync, getVolumeFromEvent]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    emitVolumeWithMuteSync(getVolumeFromEvent(touch.clientX, touch.clientY));

    const onTouchMove = (ev: TouchEvent) => {
      const t = ev.touches[0];
      if (!t) return;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (t) emitVolumeWithMuteSync(getVolumeFromEvent(t.clientX, t.clientY));
        rafRef.current = null;
      });
    };
    const cleanup = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', cleanup);
      document.removeEventListener('touchcancel', cleanup);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      dragCleanupRef.current = null;
    };
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', cleanup);
    document.addEventListener('touchcancel', cleanup);
    dragCleanupRef.current = cleanup;
  }, [emitVolumeWithMuteSync, getVolumeFromEvent]);
  const displayVolume = isMuted ? 0 : volume;
  const pct = `${displayVolume * 100}%`;
  const btnClass = `${styles.btn}${isMuted || volume === 0 ? ` ${styles.volMute}` : volume < 0.5 ? ` ${styles.volLow}` : ''}`;

  return (
    <div
      className={styles.volume}
      data-volume-control
      {...(isSliderOpen ? { 'data-slider-open': '' } : {})}
      onTouchStart={handleVolumeContainerTouch}
    >
      <div className={styles.btnWrapper}>
        <button
          type="button"
          className={btnClass}
          onClick={handleMuteClick}
        >
          <span className={styles.iconHigh}><VolumeHighIcon /></span>
          <span className={styles.iconLow}><VolumeLowIcon /></span>
          <span className={styles.iconMute}><VolumeMuteIcon /></span>
        </button>
        <Tooltip label={isMuted ? 'Включить звук' : 'Отключить звук'} shortcut="m" />
      </div>
      <div
        className={styles.sliderWrap}
        ref={sliderWrapRef}
        data-no-toggle
        data-volume-slider
        data-active-zone-interactive="true"
        data-active-zone-action="pointerdown"
        onMouseDown={handleSliderDown}
        onTouchStart={handleTouchStart}
      >
        <div
          className={styles.slider}
          role="slider"
          aria-label="Volume"
          aria-valuenow={Math.round(displayVolume * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          style={{ '--vol-pct': pct } as React.CSSProperties}
        >
          <div className={styles.track} ref={trackRef}>
            <div className={styles.fill} />
            <div className={styles.handle}>
              <GripIcon />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
